import type { ApprovalItem } from "@/types/approval";
import type { DraftPreview } from "@/types/draft";

const WORKER_URL = import.meta.env.VITE_API_URL ?? "";
const API_BASE = WORKER_URL ? `${WORKER_URL.replace(/\/$/, "")}/api` : "/api";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${path} ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export interface ApprovalActionResult {
  ok: true;
  skipped?: boolean;
  sent?: boolean;
  statusUpdateFailed?: boolean;
  messageId?: string;
  threadId?: string;
  sentAt?: number;
  item?: ApprovalItem;
  error?: string;
}

export interface ApprovalApiError {
  status: number;
  body: { error?: string; rejected?: string[]; detail?: string };
}

async function postAction(path: string, body?: unknown): Promise<ApprovalActionResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: any;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { raw: text };
  }
  if (!res.ok && res.status !== 207) {
    const err = new Error(parsed.error ?? `API ${path} ${res.status}`) as Error & {
      apiStatus: number;
      apiBody: any;
    };
    err.apiStatus = res.status;
    err.apiBody = parsed;
    throw err;
  }
  return parsed as ApprovalActionResult;
}

export const api = {
  me: async (): Promise<{ email: string } | null> => {
    const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
    if (res.status === 401) return null;
    if (!res.ok) throw new Error(`API /auth/me ${res.status}`);
    return res.json();
  },
  listInbox: () => request<{ items: ApprovalItem[] }>("/approvals/inbox"),
  approve: (id: string, options?: { sendGmail?: boolean }) =>
    postAction(`/approvals/${id}/approve`, options ?? {}),
  reject: (id: string) => postAction(`/approvals/${id}/reject`),
  comment: (id: string, comment: string) =>
    postAction(`/approvals/${id}/comment`, { comment }),
  draftPreview: (id: string) =>
    request<DraftPreview>(`/approvals/${id}/draft_preview`),
};
